"use client";

import { useTranslations } from "next-intl";
import {
  Cutting,
  CuttingType,
} from "../../portal/components/products/CuttingInfo";
import { Form, FormField, Select } from "../../theme/components/Form";
import useFormValidators from "../../theme/hooks/useFormValidators";
import { useForm } from "react-hook-form";
import FormErrors from "../../theme/components/FormErrors";
import Button from "../../theme/components/Button";
import Icon from "../../common/components/Icon";

export default function ProductCuttingForm({
  type,
  value,
  min = 0,
  max = 100,
  onCuttingChange,
}: {
  min: Number;
  max: Number;
  onCuttingChange: any;
} & Cutting) {
  const t = useTranslations();
  const { required } = useFormValidators();
  const defaultValues = {
    cutting_selected: type || "round",
    diameter_in_mm_label:
      (typeof value === "object" ? value.width : value) || null,
    width_in_mm_label:
      (typeof value === "object" ? value.width : value) || null,
    height_in_mm_label:
      (typeof value === "object" ? value.height : value) || null,
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, ...rest },
  } = useForm<any>({
    defaultValues,
  });

  const currentType = watch("cutting_selected");

  return (
    <Form
      onSubmit={handleSubmit((values) => {
        const cutting: Cutting = {
          type: values.cutting_selected,
          value:
            values.cutting_selected === CuttingType.ELLIPSE
              ? {
                  width: values.width_in_mm_label,
                  height: values.height_in_mm_label,
                }
              : values.diameter_in_mm_label,
        };
        onCuttingChange(cutting);
      })}
    >
      <div className="row">
        <div className="col-6 mb-3 col-lg-3 mb-lg-0 radio-btn-group d-flex align-items-center justify-content-center">
          <input type="radio" id="radio1" name="myradios" value="1" />
          <label
            htmlFor="radio1"
            className="radio-btn text-center w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2"
          >
            <Icon icon="circle" className="icon--xl" />
            <small className="d-flex align-items-center gap-4 justify-content-between">
              {t("cutting_template_round")}
            </small>
          </label>
        </div>
        <div className="col-6 mb-3 col-lg-3 mb-lg-0 radio-btn-group d-flex align-items-center justify-content-center">
          <input type="radio" id="radio2" name="myradios" value="1" />
          <label
            htmlFor="radio2"
            className="radio-btn text-center w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2"
          >
            <Icon icon="oval" className="icon--xl" />
            <small className="d-flex align-items-center gap-4 justify-content-between">
              {t("cutting_template_elliptical")}
            </small>
          </label>
        </div>
        <div className="col-6 mb-3 col-lg-3 mb-lg-0 radio-btn-group d-flex align-items-center justify-content-center">
          <input type="radio" id="radio3" name="myradios" value="1" />
          <label
            htmlFor="radio3"
            className="radio-btn text-center w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2"
          >
            <Icon icon="target" className="icon--xl" />
            <small className="d-flex align-items-center gap-4 justify-content-between">
              {t("cutting_template_cart_info")}
            </small>
          </label>
        </div>
        <div className="col-6 mb-3 col-lg-3 mb-lg-0 radio-btn-group d-flex align-items-center justify-content-center">
          <input type="radio" id="radio4" name="myradios" value="1" />
          <label
            htmlFor="radio4"
            className="radio-btn text-center w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2"
          >
            <Icon icon="uncut" className="icon--xl" />
            <small className="d-flex align-items-center gap-4 justify-content-between">
              {t("uncut")}
            </small>
          </label>
        </div>
        <div className="col-12 my-4">
          <FormField
            InputField={Select}
            label={t("cutting_selected")}
            options={[
              { value: "round", label: t("cutting_template_round") },
              { value: "ellipse", label: t("cutting_template_elliptical") },
              { value: "template", label: t("cutting_template_cart_info") },
            ]}
            errorMessage={errors.cutting_selected?.message as string}
            {...register("cutting_selected")}
          />
        </div>
        {currentType === CuttingType.ELLIPSE && (
          <>
            <div className="col-12 col-md-6 mb-4">
              <FormField
                label={t("width_in_mm_label")}
                min={min}
                max={max}
                type="number"
                errorMessage={errors.width_in_mm_label?.message as string}
                {...register("width_in_mm_label", {
                  required,
                })}
              />
            </div>
            <div className="col-12 col-md-6 mb-4">
              <FormField
                label={t("height_in_mm_label")}
                min={min}
                max={max}
                type="number"
                errorMessage={errors.height_in_mm_label?.message as string}
                {...register("height_in_mm_label", {
                  required,
                })}
              />
            </div>
          </>
        )}
        {currentType === CuttingType.ROUND && (
          <>
            <div className="col-12 col-md-6 mb-4">
              <FormField
                label={t("diameter_in_mm_label")}
                min={min}
                max={max}
                type="number"
                errorMessage={errors.diameter_in_mm_label?.message as string}
                {...register("diameter_in_mm_label", {
                  required,
                })}
              />
            </div>
          </>
        )}
      </div>

      <FormErrors errors={errors?.root ? { root: errors.root } : null} />
      <div className="d-flex gap-3 justify-content-end">
        <Button
          appearance="secondary"
          onClick={() => onCuttingChange(undefined)}
        >
          {t("cancel")}
        </Button>
        <div className="submit-button-container">
          <FormField type="submit" value={t("save")} />
        </div>
      </div>
    </Form>
  );
}
